import { defineStore } from 'pinia'
import { jwtDecode } from 'jwt-decode'
import Constant from '@/constants/Constant'
import RoleCode from '@/constants/RoleCode'
import { apiLogin, apiRefreshToken } from '@/services/authService'
import { usePermissionStore } from '@/stores/permission-store'

// Chu dong refresh access token truoc khi het han, tranh bi van dang lam thi het phien
const TOKEN_REFRESH_BUFFER_MS = 10 * 60 * 1000 // Goi refresh som 10 phut truoc khi access token het han
const MIN_REFRESH_DELAY_MS = 60 * 1000 // Khoang cho toi thieu giua 2 lan refresh, tranh goi lien tuc

let refreshTimerId: ReturnType<typeof setTimeout> | null = null

function clearScheduledTokenRefresh() {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId)
    refreshTimerId = null
  }
}

export const useGlobalStore = defineStore('global', {
  state: () => {
    // console.log('State: ', localStorage.getItem('sidebarMinimized') === 'true')
    let roleCode: string | null = null

    let level = 1 // Cap do phan quyen bo sung (level 2 = level 1 + quyen mo rong), mac dinh 1

    let isAdmin: boolean | null = null // La quan tri (Thi cho BOD la quan tri di)

    let fullName: string | null = null

    let username: string | null = null

    try {
      const token = localStorage.getItem(Constant.ACCESS_TOKEN)
      if (token) {
        const decoded: any = jwtDecode(token)
        roleCode = decoded?.role_code ?? null
        level = decoded?.level ?? 1
        fullName = decoded?.full_name ?? decoded?.name ?? null
        username = decoded?.sub ?? decoded?.username ?? null

        if (roleCode) {
          isAdmin = [RoleCode.BOD].includes(roleCode)
        }
      }
      if (!fullName) {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const parsed = JSON.parse(userStr)
          fullName = parsed?.full_name ?? parsed?.name ?? null
        }
      }
    } catch (e) {
      roleCode = null
    }

    // console.log(">>>roleCode: ", roleCode)

    return {
      // Lấy từ localStorage, nếu chưa có thì default = false
      isSidebarMinimized: localStorage.getItem('sidebarMinimized') === 'true',
      roleCode,
      level,
      isAdmin,
      fullName,
      username,
    }
  },

  actions: {
    toggleSidebar() {
      this.isSidebarMinimized = !this.isSidebarMinimized
      localStorage.setItem('sidebarMinimized', String(this.isSidebarMinimized))
    },
    setSidebarMinimized(val: boolean) {
      this.isSidebarMinimized = val
      localStorage.setItem('sidebarMinimized', String(val))
    },

    // Load role từ token => Load luôn ở State bên trên rồi
    // loadRoleFromToken() {
    //   try {
    //     const token = localStorage.getItem(Constant.ACCESS_TOKEN)
    //     if (!token) {
    //       this.roleCode = null
    //       return
    //     }
    //
    //     const decoded: any = jwtDecode(token)
    //     this.roleCode = decoded?.role_code ?? null
    //   } catch (e) {
    //     this.roleCode = null
    //   }
    // },

    clearAuth() {
      this.roleCode = null
      this.level = 1
      this.isAdmin = false
    },

    async login(username: string, password: string) {
      const response = await apiLogin(username, password)

      const { access_token, refresh_token, user } = response.data

      localStorage.setItem(Constant.ACCESS_TOKEN, access_token)
      if (refresh_token) {
        localStorage.setItem(Constant.REFRESH_TOKEN, refresh_token)
      }
      localStorage.setItem('user', JSON.stringify(user))

      // ⭐ đồng bộ role + level + isAdmin + fullName
      try {
        const decoded: any = jwtDecode(access_token)
        this.roleCode = decoded?.role_code ?? null
        this.level = decoded?.level ?? 1
        this.isAdmin = this.roleCode ? [RoleCode.BOD].includes(this.roleCode) : false
        this.fullName = decoded?.full_name ?? decoded?.name ?? user?.full_name ?? user?.name ?? null
        this.username = decoded?.sub ?? decoded?.username ?? null
      } catch {
        this.roleCode = null
        this.level = 1
        this.isAdmin = false
        this.fullName = null
        this.username = null
      }

      this.scheduleTokenRefresh()

      // Tải quyền thật (ma trận 1) sau khi đăng nhập; lỗi sẽ fallback Permission.ts tĩnh
      await usePermissionStore().loadMyPermissions()

      return response
    },

    logout() {
      clearScheduledTokenRefresh()

      localStorage.removeItem(Constant.ACCESS_TOKEN)
      localStorage.removeItem(Constant.REFRESH_TOKEN)
      localStorage.removeItem('user')
      sessionStorage.clear()

      // ⭐ reset auth state
      this.roleCode = null
      this.level = 1
      this.isAdmin = false
      this.fullName = null

      // Xóa quyền đã nạp
      usePermissionStore().reset()
    },

    // Len lich tu dong goi API refresh token truoc khi access token hien tai het han
    scheduleTokenRefresh() {
      clearScheduledTokenRefresh()

      const token = localStorage.getItem(Constant.ACCESS_TOKEN)
      if (!token) return

      try {
        const decoded: any = jwtDecode(token)
        const expiresAt = decoded.exp * 1000
        const delay = Math.max(expiresAt - Date.now() - TOKEN_REFRESH_BUFFER_MS, MIN_REFRESH_DELAY_MS)

        refreshTimerId = setTimeout(() => {
          this.refreshAccessToken()
        }, delay)
      } catch (e) {
        // Token khong hop le thi khong lap lich, de router guard xu ly khi dieu huong
      }
    },

    // Goi API refresh token, cap nhat access_token + refresh_token moi roi lap lich cho lan ke tiep
    async refreshAccessToken() {
      const refreshToken = localStorage.getItem(Constant.REFRESH_TOKEN)
      if (!refreshToken) return

      try {
        const response = await apiRefreshToken(refreshToken)
        const { access_token, refresh_token } = response.data

        localStorage.setItem(Constant.ACCESS_TOKEN, access_token)
        if (refresh_token) {
          localStorage.setItem(Constant.REFRESH_TOKEN, refresh_token)
        }

        const decoded: any = jwtDecode(access_token)
        this.roleCode = decoded?.role_code ?? null
        this.level = decoded?.level ?? 1
        this.isAdmin = this.roleCode ? [RoleCode.BOD].includes(this.roleCode) : false
        this.fullName = decoded?.full_name ?? decoded?.name ?? this.fullName

        this.scheduleTokenRefresh()
      } catch (e) {
        // Refresh that bai (vi du refresh token cung het han) -> de lan goi API tiep theo gap 401 va dang xuat nhu binh thuong
        clearScheduledTokenRefresh()
      }
    },
  },
})
